import { useState, useEffect } from 'react';
import SinapiService from '../services/SinapiService';

function formatCurrency(value) {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function SinapiComposicaoModal({ codigo, estado, tipoPreco = 'NAO_DESONERADO', onClose, onAdd }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantidade, setQuantidade] = useState(1);

  useEffect(() => {
    if (!codigo) return;
    setLoading(true);
    SinapiService.getComposicao(codigo, estado, tipoPreco)
      .then((res) => setData(res))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [codigo, estado, tipoPreco]);

  if (!codigo) return null;

  const handleAdd = () => {
    if (data?.composicao) {
      onAdd({
        codigo: data.composicao.codigo,
        descricao: data.composicao.descricao,
        unidade: data.composicao.unidade,
        preco: data.composicao.preco,
        quantidade,
      });
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-lg font-bold text-gray-900">Detalhes da Composição</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orca-600" />
            </div>
          ) : data?.composicao ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">Código</span>
                  <p className="text-lg font-bold text-orca-700">{data.composicao.codigo}</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">Unidade</span>
                  <p className="text-lg font-semibold text-gray-800">{data.composicao.unidade || '-'}</p>
                </div>
                <div className="md:col-span-2">
                  <span className="text-xs font-medium text-gray-500 uppercase">Descrição</span>
                  <p className="text-sm text-gray-700 mt-1">{data.composicao.descricao}</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">Preço ({estado})</span>
                  <p className="text-lg font-bold text-green-600">{formatCurrency(data.composicao.preco)}</p>
                </div>
                {data.composicao.referencia && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase">Referência</span>
                    <p className="text-sm text-gray-700">{data.composicao.referencia}</p>
                  </div>
                )}
              </div>

              {data.itens && data.itens.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Composição Analítica</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50 text-left">
                          <th className="px-3 py-2 font-semibold text-gray-600 text-xs">Código</th>
                          <th className="px-3 py-2 font-semibold text-gray-600 text-xs">Descrição</th>
                          <th className="px-3 py-2 font-semibold text-gray-600 text-xs">Unid</th>
                          <th className="px-3 py-2 font-semibold text-gray-600 text-xs text-right">Coef</th>
                          <th className="px-3 py-2 font-semibold text-gray-600 text-xs text-right">Preço Unit</th>
                          <th className="px-3 py-2 font-semibold text-gray-600 text-xs text-right">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {data.itens.map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-600 text-xs">{item.item_codigo}</td>
                            <td className="px-3 py-2 text-gray-700 text-xs max-w-[200px] truncate">
                              {item.insumo_descricao || item.item_descricao || '-'}
                            </td>
                            <td className="px-3 py-2 text-gray-600 text-xs">{item.insumo_unidade || item.item_unidade || '-'}</td>
                            <td className="px-3 py-2 text-gray-700 text-xs text-right">{item.coeficiente?.toFixed(4)}</td>
                            <td className="px-3 py-2 text-gray-700 text-xs text-right">{formatCurrency(item.preco_insumo)}</td>
                            <td className="px-3 py-2 text-gray-700 text-xs text-right font-medium">{formatCurrency(item.subtotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                <label className="text-sm font-medium text-gray-700">Quantidade:</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={quantidade}
                  onChange={(e) => setQuantidade(parseFloat(e.target.value) || 1)}
                  className="w-24 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500"
                />
                <button
                  onClick={handleAdd}
                  className="ml-auto px-6 py-2.5 text-sm font-semibold text-white bg-orca-600 rounded-lg hover:bg-orca-700 cursor-pointer transition-colors"
                >
                  Adicionar ao Orçamento
                </button>
              </div>
            </>
          ) : (
            <p className="text-center text-gray-500 py-8">Composição não encontrada</p>
          )}
        </div>
      </div>
    </div>
  );
}
